<template>
  <input
    ref="input"
    :value="localDisplayValue"
    type="text"
    class="form-control"
    :placeholder="computedPlaceholder"
    :disabled="disabled"
    @input="handleInput"
    @keypress="validateKeypress"
    @focus="handleFocus"
    @blur="handleBlur"
  />
</template>

<script>
export default {
  name: 'MonetaryInput',
  props: {
    modelValue: {
      type: [Number, null],
      default: null
    },
    optional: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localDisplayValue: '',
      isFocused: false
    };
  },
  computed: {
    computedPlaceholder() {
      if (this.placeholder) {
        return this.placeholder;
      }
      return this.optional ? 'Leave empty for no default' : '';
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue) {
        // Only update display if not currently focused
        if (!this.isFocused) {
          this.updateDisplayValue(newValue);
        }
      }
    }
  },
  methods: {
    updateDisplayValue(value) {
      if (value === null) {
        this.localDisplayValue = '';
        return;
      }

      // Convert cents to dollars, then format with $ prefix, commas and .00
      const dollars = value / 100;
      this.localDisplayValue = this.formatCurrency(dollars);
    },
    formatCurrency(valueInDollars) {
      // Format number with $ prefix, commas and always show .00
      const parts = valueInDollars.toFixed(2).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts.join('.');
    },
    validateKeypress(event) {
      const key = event.key;
      const currentValue = event.target.value;

      // Allow numbers
      if (/^\d$/.test(key)) {
        // Check if adding this digit would exceed 2 decimal places
        const decimalIndex = currentValue.indexOf('.');
        if (decimalIndex !== -1) {
          const cursorPosition = event.target.selectionStart;
          // If cursor is after the decimal point
          if (cursorPosition > decimalIndex) {
            const decimalPlaces = currentValue.substring(decimalIndex + 1).length;
            if (decimalPlaces >= 2) {
              event.preventDefault();
              return;
            }
          }
        }
        return;
      }

      // Allow decimal point only if there isn't one already
      if (key === '.' && !currentValue.includes('.')) {
        return;
      }

      // Prevent all other characters (no minus sign for money)
      event.preventDefault();
    },
    handleFocus(event) {
      this.isFocused = true;

      // Remove $ prefix, commas, and .00 for editing
      let value = this.localDisplayValue.replace(/[$,]/g, '');

      // Remove .00 suffix if present
      if (value.endsWith('.00')) {
        value = value.slice(0, -3);
      }

      this.localDisplayValue = value;

      // Auto-select all text for easy overwriting
      this.$nextTick(() => {
        event.target.select();
      });
    },
    handleBlur() {
      this.isFocused = false;

      // Strip $ and commas for processing
      let value = this.localDisplayValue.replace(/[$,]/g, '');

      // If empty (or just "$") and optional, emit null
      if (value === '' && this.optional) {
        this.$emit('update:modelValue', null);
        this.localDisplayValue = '';
        this.$emit('blur');
        return;
      }

      // If empty (or just "$") and not optional, emit 0
      if (value === '') {
        this.$emit('update:modelValue', 0);
        this.localDisplayValue = this.formatCurrency(0);
        this.$emit('blur');
        return;
      }

      // Clean up incomplete decimal entries
      if (value === '.' || value.endsWith('.')) {
        value = value.replace(/\.$/, '');
      }

      // Parse the number (in dollars)
      const parsedValueDollars = parseFloat(value);

      // Emit and format the value
      if (!isNaN(parsedValueDollars)) {
        // Convert dollars to cents before emitting
        const cents = Math.round(parsedValueDollars * 100);
        this.$emit('update:modelValue', cents);
        this.localDisplayValue = this.formatCurrency(parsedValueDollars);
      } else {
        // If invalid, revert to the model value
        this.updateDisplayValue(this.modelValue);
      }

      this.$emit('blur');
    },
    handleInput(event) {
      let value = event.target.value;

      // Strip any invalid characters that might have been pasted (no $ while typing)
      value = value.replace(/[^\d.]/g, '');

      // Ensure only one decimal point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      // Limit to 2 decimal places
      if (parts.length === 2 && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].substring(0, 2);
      }

      // Update the local display value (no $ while typing)
      this.localDisplayValue = value;

      // If empty and optional, emit null
      if (value === '' && this.optional) {
        this.$emit('update:modelValue', null);
        return;
      }

      // If empty and not optional, emit 0
      if (value === '') {
        this.$emit('update:modelValue', 0);
        return;
      }

      // Allow intermediate states like "30." or "30.0" while typing
      // Don't emit for incomplete values ending with "."
      if (value === '.' || value.endsWith('.')) {
        return;
      }

      // Parse the number (in dollars)
      const parsedValueDollars = parseFloat(value);

      // Emit the parsed value if valid (convert to cents)
      if (!isNaN(parsedValueDollars)) {
        const cents = Math.round(parsedValueDollars * 100);
        this.$emit('update:modelValue', cents);
      }
    }
  },
  emits: ['update:modelValue', 'blur']
}
</script>

<style scoped>
/* Inherits .form-control styles from global.css */
</style>
